import type { Locale } from '@/i18n/locales';

import { baseApi } from './baseApi';
import type {
  AboutUsContent,
  Branding,
  Car,
  CompanyService,
  ContactChannels,
  CustomerReview,
  LandingSection,
  Page,
  PublicPaymentSettings,
  PublicThemeColors,
  Review,
  ReviewInvitation,
  ReviewSortKey,
  ReviewSortOrder,
  ReviewSummary,
  SubmitReviewRequest,
} from './types';

export interface CustomerReviewsQuery {
  tripSlug?: string;
  sort?: ReviewSortKey;
  order?: ReviewSortOrder;
  page: number;
  size: number;
}

/**
 * The administrator-managed parts of the site.
 *
 * All of these are optional in practice: an operator who has configured nothing
 * still gets a working site, and each consumer is expected to handle an empty
 * result by hiding its section rather than showing an error.
 */
export const contentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    cars: builder.query<Car[], void>({
      query: () => 'public/cars',
      providesTags: ['Car'],
    }),

    contactChannels: builder.query<ContactChannels, void>({
      query: () => 'public/content/contact',
      providesTags: ['Settings'],
    }),

    // These three vary their response by `Accept-Language` (see
    // `PublicContentController`), so the locale is taken as the query argument
    // rather than left implicit: RTK Query's cache key is endpoint + args, not
    // headers, so a `void`-arg query would share one cache entry across both
    // locales and "stick" to whichever one was fetched first. The explicit
    // `Accept-Language` header (rather than relying on the shared
    // `baseApi` default derived from `document.documentElement.lang`) also
    // sidesteps a timing race on the very first request right after a locale
    // switch, since `locale` here comes from the synchronous `useLocale()`.
    branding: builder.query<Branding, Locale>({
      query: (locale) => ({ url: 'public/content/branding', headers: { 'Accept-Language': locale } }),
      providesTags: ['Settings'],
    }),

    landingSections: builder.query<LandingSection[], Locale>({
      query: (locale) => ({ url: 'public/content/landing', headers: { 'Accept-Language': locale } }),
      providesTags: ['Content'],
    }),

    reviews: builder.query<Review[], void>({
      query: () => 'public/reviews',
      providesTags: ['Review'],
    }),

    activeTheme: builder.query<PublicThemeColors, void>({
      query: () => 'public/content/theme',
      providesTags: ['Settings'],
    }),

    companyServices: builder.query<CompanyService[], Locale>({
      query: (locale) => ({ url: 'public/company-services', headers: { 'Accept-Language': locale } }),
      providesTags: ['CompanyService'],
    }),

    aboutUsContent: builder.query<AboutUsContent, void>({
      query: () => 'public/content/about',
      providesTags: ['AboutUs'],
    }),

    // Off by default — the booking dialog falls back to today's unpaid flow
    // until an administrator turns this on.
    paymentSettings: builder.query<PublicPaymentSettings, void>({
      query: () => 'public/content/payments',
      providesTags: ['Settings'],
    }),

    // Native customer reviews — distinct from `reviews` above, which stays
    // TripAdvisor's own feed.
    customerReviewsSummary: builder.query<ReviewSummary, void>({
      query: () => 'public/customer-reviews/summary',
      providesTags: ['CustomerReview'],
    }),

    customerReviews: builder.query<Page<CustomerReview>, CustomerReviewsQuery>({
      query: ({ tripSlug, sort, order, page, size }) => ({
        url: 'public/customer-reviews',
        params: { page, size, ...(tripSlug ? { tripSlug } : {}), ...(sort ? { sort } : {}), ...(order ? { order } : {}) },
      }),
      providesTags: ['CustomerReview'],
    }),

    reviewInvitation: builder.query<ReviewInvitation, string>({
      query: (token) => `public/customer-reviews/invitation/${encodeURIComponent(token)}`,
    }),

    submitReview: builder.mutation<CustomerReview, { token: string; body: SubmitReviewRequest }>({
      query: ({ token, body }) => ({
        url: `public/customer-reviews/invitation/${encodeURIComponent(token)}/submit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CustomerReview'],
    }),
  }),
});

export const {
  useCarsQuery,
  useContactChannelsQuery,
  useBrandingQuery,
  useLandingSectionsQuery,
  useReviewsQuery,
  useActiveThemeQuery,
  useCompanyServicesQuery,
  useAboutUsContentQuery,
  usePaymentSettingsQuery,
  useCustomerReviewsSummaryQuery,
  useCustomerReviewsQuery,
  useReviewInvitationQuery,
  useSubmitReviewMutation,
} = contentApi;
